/**
 * MyClips V3 — Faz L-2: Icon primitive
 *
 * Tek source of truth — tüm V3 component'leri bunu kullansın.
 * Stroke 1.75px, round caps/joins, currentColor fill, 24×24 viewBox.
 * Premium standart (component ajanı spec'inden).
 *
 * Kullanım:
 *   <Icon name="heart" size={28} filled />
 *   <Icon name="comment" size={24} />
 *   <Icon name="bookmark" filled accent />
 *
 * Props:
 *   name       — glyph key (aşağıdaki GLYPHS listesinden)
 *   size       — px cinsinden (default 24)
 *   filled     — dolu varyant (heart, bookmark, star)
 *   accent     — brand color kullan (default: currentColor)
 *   className  — ek class'lar
 *   ariaLabel  — set'liyse role=img + aria-label; yoksa aria-hidden
 *
 * Tasarım kararı:
 *   ikon sayısını minimal tut — V3'ün ihtiyacı olan 14 glyph. Lucide'i
 *   eklemek 30KB+; inline SVG ile ~2KB. Yeni ikon gerektiğinde buraya
 *   eklenir — ekosistem tutarlılığı sağlanır.
 */

import React from "react";

const STROKE = 1.75;
const VIEWBOX = "0 0 24 24";

/* Glyph kütüphanesi — path tanımları.
   Her glyph iki varyant içerebilir: outline (default) + filled (opsiyonel).
   Filled yoksa outline'a geri düşer. */
const GLYPHS = {
  heart: {
    outline: (
      <path d="M12 21s-7-4.35-9.33-9.05C1.17 8.46 3.1 4.5 7 4.5c2.1 0 3.7 1.15 5 3 1.3-1.85 2.9-3 5-3 3.9 0 5.83 3.96 4.33 7.45C19 16.65 12 21 12 21z" />
    ),
    filled: (
      <path d="M12 21s-7-4.35-9.33-9.05C1.17 8.46 3.1 4.5 7 4.5c2.1 0 3.7 1.15 5 3 1.3-1.85 2.9-3 5-3 3.9 0 5.83 3.96 4.33 7.45C19 16.65 12 21 12 21z" fill="currentColor" />
    ),
  },
  bookmark: {
    outline: (
      <path d="M6 3h12v18l-6-4-6 4V3z" />
    ),
    filled: (
      <path d="M6 3h12v18l-6-4-6 4V3z" fill="currentColor" />
    ),
  },
  comment: {
    outline: (
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    ),
  },
  send: {
    outline: (
      <>
        <path d="m22 2-7 20-4-9-9-4z" />
        <path d="M22 2 11 13" />
      </>
    ),
  },
  star: {
    outline: (
      <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    ),
    filled: (
      <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor" />
    ),
  },
  play: {
    outline: (
      <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" />
    ),
    filled: (
      <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" />
    ),
  },
  pause: {
    outline: (
      <>
        <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" />
        <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" />
      </>
    ),
  },
  volume: {
    outline: (
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      </>
    ),
    filled: (
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      </>
    ),
  },
  "volume-off": {
    outline: (
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <line x1="23" y1="9" x2="17" y2="15" />
        <line x1="17" y1="9" x2="23" y2="15" />
      </>
    ),
  },
  search: {
    outline: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </>
    ),
  },
  plus: {
    outline: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    ),
  },
  check: {
    outline: (
      <polyline points="20 6 9 17 4 12" />
    ),
  },
  x: {
    outline: (
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>
    ),
  },
  more: {
    outline: (
      <>
        <circle cx="12" cy="12" r="1" fill="currentColor" />
        <circle cx="19" cy="12" r="1" fill="currentColor" />
        <circle cx="5"  cy="12" r="1" fill="currentColor" />
      </>
    ),
  },
  chevronDown: {
    outline: (
      <polyline points="6 9 12 15 18 9" />
    ),
  },
  chevronRight: {
    outline: (
      <polyline points="9 18 15 12 9 6" />
    ),
  },
  music: {
    outline: (
      <>
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </>
    ),
  },
  share: {
    outline: (
      <>
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </>
    ),
  },
  sparkle: {
    outline: (
      <>
        <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
        <path d="m7 7 2 2M15 15l2 2M7 17l2-2M15 9l2-2" />
      </>
    ),
    filled: (
      <path d="M12 2l2.09 5.26L19 8l-4.91 2.74L12 16l-2.09-5.26L5 8l4.91-.74L12 2z" fill="currentColor" />
    ),
  },
};

export type IconProps = {
  // `any` idi. `keyof typeof GLYPHS` YAZILMADI — gövde bilinmeyen adı
  // açıkça destekliyor ("Bilinmeyen ikon — sessiz null"), dar tip o dalı
  // ölü koda çevirir ve çağıranları yalanlardı.
  name: string;
  size?: number;
  filled?: boolean;
  accent?: boolean;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  [x: string]: any;
};

export function Icon({
  name,
  size = 24,
  filled = false,
  accent = false,
  className = "",
  style,
  ariaLabel,
  ...rest
}: IconProps) {
  // `GLYPHS[name]` ham hâliyle TS7053 veriyordu (string ile sabit-anahtarlı
  // nesneyi indeksleme). Genişletici okuma, `!glyph` guard'ını CANLI tutar —
  // `keyof` daraltması onu ölü koda çevirirdi.
  const sozluk = GLYPHS as Record<
    string,
    { outline: React.ReactNode; filled?: React.ReactNode } | undefined
  >;
  const glyph = sozluk[name];
  if (!glyph) {
    // Bilinmeyen ikon — sessiz null (dev'de console.warn eklenebilir)
    return null;
  }
  const content = filled && glyph.filled ? glyph.filled : glyph.outline;

  const a11yProps = ariaLabel
    ? { role: "img", "aria-label": ariaLabel }
    : { "aria-hidden": true, focusable: false };

  const color = accent ? "var(--mlv3-accent)" : "currentColor";

  return (
    <svg
      width={size}
      height={size}
      viewBox={VIEWBOX}
      fill="none"
      stroke={color}
      strokeWidth={STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ color, ...style }}
      {...a11yProps}
      {...rest}
    >
      {content}
    </svg>
  );
}

/** Mevcut glyph listesi — runtime introspection / test için */
export const ICON_NAMES = Object.keys(GLYPHS);

export default Icon;
